"use client";
import dayjs from "dayjs";
import { useMemo } from "react";

import { apiGetHotEvents } from "@/api/common";
import { apiGetSportEvents, apiGetWorldCupSportEvents } from "@/api/event";
import {
    apiGetCricketLiveEventsList,
    apiGetTopicEvents,
    type CricketLiveEvent,
    HOME_STATIC_QUERY_GC_TIME,
    HOME_STATIC_QUERY_STALE_TIME,
} from "@/api/home";
import HomeImagePopup from "@/components/home/home-image-popup/home-image-popup";
import { HomeEventListSkeleton } from "@/components/home/HomeEventListSkeleton";
import { SpotlightCarousel } from "@/components/home/spotlight-carousel";
import { SpotlightCarouselSkeleton } from "@/components/home/SpotlightCarouselSkeleton";
import { LoadMoreButton } from "@/components/load-more-button";
import EventCard from "@/components/platform/event-card";
import SubTopicTabs from "@/components/platform/sub-topic-tabs";
import EmptyState from "@/components/portfolio/empty-state";
import { useLocalFirstInfiniteQuery, useLocalFirstQuery, useLocalFirstUserScope } from "@/hooks/use-local-first-cache";
import { localFirstCacheKeys, localFirstQueryKeys } from "@/lib/query-local-first";
import { type TopicItem, useTopicStore } from "@/store/topic";
import { navigateToEventDetail, useAppRouter } from "@/utils/router";
import { get } from "@/utils/utils";

type TopicEventsPage = { records: any[]; pageNum: number; hasMore: boolean };

function isWorldCupGamesTopic( topicChains: TopicItem[] ) {
    return topicChains.some( (
        topic,
        index,
    ) => ( topic.name === "World Cup" && topicChains[index + 1]?.name === "Games" ) );
}

function getTopicEventsCacheMode(
    isSportTopic: boolean,
    isWorldCupGamesTopicSelected: boolean,
): "normal" | "sport" | "worldCupGames" {
    if ( !isSportTopic ) {
        return "normal";
    }

    return isWorldCupGamesTopicSelected ? "worldCupGames" : "sport";
}

// ============ Main Page Component ============
export default function Home() {
    const router = useAppRouter();
    const { normalTopicChains, sportTimeRange } = useTopicStore();
    const { dimensionKey: userDimensionKey } = useLocalFirstUserScope();
    const isTrending = useMemo( () => {
        return normalTopicChains.length > 0 && normalTopicChains[0].name === "Trending";
    }, [ normalTopicChains ] );
    const { data: spotlightEvents = [], isLoading: isSpotlightLoading } = useLocalFirstQuery( {
        storageKey: localFirstCacheKeys.user.cricketLiveEvents( userDimensionKey ),
        queryKey: localFirstQueryKeys.cricketLiveEvents( userDimensionKey ),
        queryFn: apiGetCricketLiveEventsList,
        staleTime: HOME_STATIC_QUERY_STALE_TIME,
        gcTime: HOME_STATIC_QUERY_GC_TIME,
        refetchInterval: 1000 * 5,
        enabled: isTrending,
    } );

    // 判断当前选中的主题是否是运动类
    const isSportTopic = useMemo( () => normalTopicChains.at( -1 )?.ifSport === true, [ normalTopicChains ] );
    const isWorldCupGamesTopicSelected = useMemo( () => isWorldCupGamesTopic( normalTopicChains ), [
        normalTopicChains,
    ] );

    const currentTopicId = normalTopicChains.at( -1 )?.id;
    const topicEventsCacheMode = getTopicEventsCacheMode( isSportTopic, isWorldCupGamesTopicSelected );
    const topicEventsStorageKey = localFirstCacheKeys.user.topicEvents( userDimensionKey, {
        topicId: currentTopicId,
        mode: topicEventsCacheMode,
        timeRange: sportTimeRange,
    } );

    const isForYouTopic = useMemo( () => normalTopicChains.at( -1 )?.name?.toLocaleLowerCase() === "for you", [
        normalTopicChains,
    ] );

    // 获取事件列表数据（使用 react-query 无限滚动）
    const eventsQuery = useLocalFirstInfiniteQuery( {
        storageKey: topicEventsStorageKey,
        queryKey: localFirstQueryKeys.topicEvents( {
            topicId: currentTopicId,
            mode: topicEventsCacheMode,
            sportTimeRange,
            userDimensionKey,
        } ),
        queryFn: async ( { pageParam } ) => {
            const currentPage = typeof pageParam === "number" ? pageParam : 1;
            if ( !normalTopicChains.length ) {
                return { records: [], pageNum: currentPage, hasMore: false };
            }

            const topicId = currentTopicId!;

            if ( isSportTopic ) {
                const [ isOk, res ] = isWorldCupGamesTopicSelected
                    ? await apiGetWorldCupSportEvents( {
                        pageNum: currentPage,
                        pageSize: 20,
                        timeRange: sportTimeRange,
                    } )
                    : await apiGetSportEvents( {
                        topicId: Number( topicId ),
                        pageNum: currentPage,
                        pageSize: 20,
                        timeRange: sportTimeRange,
                    } );
                if ( isOk ) {
                    const rawData = get( res, "data", [] );
                    const rawRecords = Array.isArray( rawData ) ? rawData : [];
                    return {
                        records: rawRecords,
                        pageNum: currentPage,
                        hasMore: rawRecords.length >= 20, // 如果返回的数据量 >= pageSize，可能还有更多
                    };
                }

                throw new Error( get( res, "msg", "Failed to fetch sport events" ) );
            } else if ( isForYouTopic ) {
                const [ isOk, res ] = await apiGetHotEvents( { pageNum: currentPage, pageSize: 20 } );
                if ( isOk ) {
                    const data = get( res, "data", [] );
                    return { records: data, pageNum: currentPage, hasMore: data.length >= 20 };
                }

                throw new Error( get( res, "msg", "Failed to fetch hot events" ) );
            } else {
                // 普通主题使用 apiGetTopicEvents
                const [ isOk, res ] = await apiGetTopicEvents( { id: topicId, pageNum: currentPage, pageSize: 20 } );
                if ( isOk ) {
                    const data = get( res, "data", [] );
                    return {
                        records: data,
                        pageNum: currentPage,
                        hasMore: data.length >= 20, // 如果返回的数据量 >= pageSize，可能还有更多
                    };
                }

                throw new Error( get( res, "msg", "Failed to fetch topic events" ) );
            }
        },
        getNextPageParam: ( lastPage: TopicEventsPage ) => {
            return lastPage.hasMore ? lastPage.pageNum + 1 : undefined;
        },
        initialPageParam: 1,
        // Poll to refresh existing pages while keeping stored pageParam values.
        refetchInterval: 1000 * 5,
        enabled: normalTopicChains.length > 0,
    } );

    // 解构需要的属性
    // 注意：只使用 isLoading 判断初始加载状态，而不是 isFetching
    // 这样点击"加载更多"时不会触发整个列表重新渲染
    const { data: eventsData, isLoading } = eventsQuery;

    const loading = isLoading;

    // 合并所有页的数据
    const events = useMemo( () => eventsData?.pages.flatMap( page => page.records ) ?? [], [ eventsData ] );

    // 体育赛事按天分组
    const groupedSportEvents = useMemo( () => {
        if ( !isSportTopic || events.length === 0 ) return [];

        const groups: { date: string; dateLabel: string; events: any[] }[] = [];

        events.forEach( ( event ) => {
            const eventDate = dayjs( event.occurTime );
            const dateKey = eventDate.format( "YYYY-MM-DD" );
            const dateLabel = eventDate.format( "ddd, MMMM D" ); // "Sat, November 15"
            const existingGroup = groups.find( g => g.date === dateKey );
            if ( existingGroup ) {
                existingGroup.events.push( event );
            } else {
                groups.push( { date: dateKey, dateLabel, events: [ event ] } );
            }
        } );

        return groups;
    }, [ isSportTopic, events ] );

    const handleSpotlightNavigate = ( event: CricketLiveEvent ) => {
        if ( !event.eventNo ) return;
        void navigateToEventDetail( router, {
            eventNo: event.eventNo,
            isMainPageEvent: true,
            ...( event.eventCategory !== undefined && { eventCategory: event.eventCategory } ),
        } );
    };

    return (
        <>
            {/* 内容区域 - 需要给固定定位的 TopicTabs 留出空间 */}
            <div>
                {/* 实时赛况焦点卡 */}
                {isTrending && isSpotlightLoading && spotlightEvents.length === 0 && <SpotlightCarouselSkeleton />}
                {isTrending && spotlightEvents.length > 0 && (
                    <SpotlightCarousel events={spotlightEvents} onEventNavigate={handleSpotlightNavigate} />
                )}

                <SubTopicTabs />

                {/* Loading state */}
                {loading && <HomeEventListSkeleton ifSport={isSportTopic} />}

                {/* Event list */}
                {!loading && (
                    <div className="mb-[30px] mt-3 flex flex-col gap-3">
                        {events.length === 0 && <EmptyState text="No events found" />}

                        {/* 体育赛事按天分组渲染 */}
                        {isSportTopic && groupedSportEvents.map( ( group ) => (
                            <div key={group.date}>
                                {/* 日期标题 */}
                                <div className="text-base font-[600] text-t1">{group.dateLabel}</div>
                                {/* 该天的赛事列表 */}
                                <div className="flex flex-col gap-3 mt-3">
                                    {group.events.map( ( event, index ) => (
                                        <EventCard
                                            key={`${event.eventNo || event.id}-${index}`}
                                            eventInfo={event}
                                            ifSport={isSportTopic}
                                        />
                                    ) )}
                                </div>
                            </div>
                        ) )}

                        {/* 普通事件列表 */}
                        {!isSportTopic
                            && events.map( ( event, index ) => (
                                <EventCard
                                    key={`${event.eventNo || event.id}-${index}`}
                                    eventInfo={event}
                                    ifSport={isSportTopic}
                                    imageSize="large"
                                />
                            ) )}

                        {/* Load More Button */}
                        <LoadMoreButton query={eventsQuery} />
                    </div>
                )}
            </div>

            {/* 首页图片弹窗 */}
            <HomeImagePopup />
        </>
    );
}
